import { defineDocument } from './types.js'
import {
  ALL_TABS, BASIC_ACTIONS, baseColumns, baseFilters, col, f, interstate, jobType, quantityLines, taxUnit,
} from './common.js'

/** docs/08 §1 — stock moving between the warehouse and a site. */
export const materialTransfer = defineDocument({
  key: 'material_transfer',
  group: 'Inventory',
  blurb: 'Transfer material from stores to a project site. You can also transfer material out from a site.',
  route: 'mat_trfs',
  label: 'Material transfer',
  labelPlural: 'Material transfers',
  numbering: 'MATERIAL_TRANSFER',
  numberPrefix: 'MT-{CO} ',
  workflow: 'MATERIAL_TRANSFER',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Material transfer',
    subtitle: 'Which way is the material moving, and for which project?',
    fields: [
      f.radio('transferType', 'Type of transfer', [
        ['WAREHOUSE_TO_SITE', 'Warehouse to site'],
        ['SITE_TO_WAREHOUSE', 'Site to warehouse'],
        ['SITE_TO_SITE', 'Site to site'],
        ['RESERVED_TO_UNRESERVED', 'Reserved to unreserved'],
      ]),
      f.look('projectId', 'Project', 'project', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.choice('transferType', 'Type of transfer', [
          ['WAREHOUSE_TO_SITE', 'Warehouse to site'],
          ['SITE_TO_WAREHOUSE', 'Site to warehouse'],
          ['SITE_TO_SITE', 'Site to site'],
          ['RESERVED_TO_UNRESERVED', 'Reserved to unreserved'],
        ], false),
        f.look('projectId', 'Project', 'project', true),
        f.look('customerId', 'Customer', 'customer'),
        f.date('documentDate', 'Transfer date', true),
        f.look('warehouseId', 'From warehouse', 'warehouse'),
        // What the transfer answers. The type declared `allocatesFrom:
        // ['material_request']` with no field to resolve it, so "Pull the open
        // lines" was a button that always answered "nothing" — the same bug
        // the delivery challan had.
        { ...f.look('materialRequestId', 'Against material request', 'material_request'),
          hint: 'The site request this transfer answers. Its open lines can be pulled straight in.' },
        f.look('toProjectId', 'To project', 'project'),
        f.look('issuedToId', 'Handed to', 'employee'),
        f.text('vehicleNo', 'Vehicle no'),
        // A transfer moves goods on a public road exactly as a challan does,
        // and the setting has always said the number is shown on both. Only
        // the challan had the fields.
        { ...f.text('ewayBillNo', 'e-Way bill no'), requiresSetting: 'ewaybill_enabled' },
        { ...f.choice('ewayBillStatus', 'e-Way bill status', [
          ['', 'Not raised'], ['GENERATED', 'Generated'], ['CANCELLED', 'Cancelled'],
          ['EXPIRED', 'Expired'],
        ], false), requiresSetting: 'ewaybill_enabled' },
        f.text('deliveryCoordinator', 'Delivery coordinator'),
        f.text('deliveryContactNo', 'Delivery contact no'),
        jobType(),
        taxUnit(),
        interstate(),
        f.notes(),
      ] },
      { key: 'delivery', n: '02', title: 'Delivery', fields: [
        f.look('dispatchAddressId', 'Dispatch from', 'address'),
        f.look('deliveryAddressId', 'Deliver to', 'address'),
      ] },
    ],
  },
  lines: {
    ...quantityLines('Materials transferred', '03', 'Transferred'),
    columns: [
      col.seq(), col.item(), col.desc(), col.brand(),
      { key: 'fromReserved', label: 'From reserved', type: 'qty', width: 128, align: 'right', editable: true,
        hint: 'Quantity taken from what was reserved for this project rather than from free stock.' },
      col.qty('Transferred'), col.unit(), col.rate(), col.hsn(), col.tax(), col.amount(),
    ],
    sources: [
      { key: 'stock', label: 'Pick from stock', source: 'stock' },
      { key: 'excel', label: 'Import from Excel', source: 'excel' },
    ],
  },
  columns: baseColumns(null, 'Value'),
  filters: [
    ...baseFilters(null),
    { key: 'warehouseId', label: 'Warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
    { key: 'jobTypeId', label: 'Job type', control: 'lookup', lookup: { entity: 'job_type' } },
  ],
  actions: BASIC_ACTIONS,
  rules: ['excess_purchase_quantity', 'insufficient_stock'],
  requiresSetting: 'inventory_enabled',
  allocatesFrom: ['material_request'],
  createsNext: [{ docType: 'delivery_challan', label: 'Delivery challan', whenStatus: 'APPROVED' }],
} as const)

/** docs/08 §2 — material issued from, or returned to, a site store. */
export const siteInventoryRecord = defineDocument({
  key: 'site_inventory_record',
  group: 'Inventory',
  blurb: 'Record material inward and outward for a project site store.',
  route: 'site_issues',
  label: 'Site inventory record',
  labelPlural: 'Site inventory records',
  numbering: 'SITE_INVENTORY_RECORD',
  numberPrefix: 'Site Inven: ',
  numberWidth: 3,
  workflow: 'SITE_INVENTORY_RECORD',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Site inventory record',
    fields: [
      f.radio('recordType', 'What is happening?', [['ISSUE', 'Issue from the store'], ['RETURN', 'Return to the store']]),
      f.look('projectId', 'Project', 'project', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.choice('recordType', 'What is happening?', [['ISSUE', 'Issue from the store'], ['RETURN', 'Return to the store']], false),
        f.look('projectId', 'Project', 'project', true),
        f.look('customerId', 'Customer', 'customer'),
        f.date('documentDate', 'Date', true),
        f.look('subcontractorId', 'Subcontractor', 'subcontractor'),
        f.look('issuedToId', 'Issued to', 'employee'),
        jobType(),
        taxUnit(),
        f.notes(),
      ] },
    ],
  },
  lines: quantityLines('Materials', '02', 'Quantity'),
  columns: baseColumns(null, 'Value'),
  filters: baseFilters(null),
  actions: BASIC_ACTIONS,
  // The company setting says the feature exists at all; the rule says whether
  // THIS project uses it, which is the vendor's own condition
  // (docs/video-notes/06).
  rules: ['site_store_not_tracked', 'insufficient_stock'],
  requiresSetting: 'site_inventory_enabled',
} as const)

/** Where the part of an issue that was not used went (docs/24 §3.9). */
export const WASTAGE_REASONS = [
  ['NORMAL', 'Normal wastage — off-cuts, handling'],
  ['DAMAGE', 'Damaged at site'],
  ['THEFT', 'Theft'],
  ['LOSS', 'Lost'],
  ['EXCESS_CUTTING', 'Excess cutting'],
] as const satisfies ReadonlyArray<[string, string]>

/**
 * docs/24 §3.9 — what happened to what was issued.
 *
 * "Material issued to site 20 m; actual material used 18 m; balance 2 m — it
 * can be wastage, theft, loss; or it will return to inventory." Each line
 * settles part of an issue: how much went into the work, how much was lost
 * and why, and how much came back to the store. Whatever is not accounted for
 * stays open on the issue as the balance still with the crew, and turns up on
 * the next consumption record.
 *
 * What comes back is stock again — a lot at the site store, or at the
 * warehouse named — and is issued to the next job like anything else. Used
 * and wastage are measured against what the measured work says should have
 * gone in: the work progress (JMR) named on the header.
 */
export const materialConsumption = defineDocument({
  key: 'material_consumption',
  group: 'Inventory',
  blurb: 'Account for what was issued to site: used, wasted (and why), and returned to the store.',
  route: 'mat_consumption',
  label: 'Material consumption',
  labelPlural: 'Material consumption records',
  numbering: 'MATERIAL_CONSUMPTION',
  numberPrefix: 'MC/',
  workflow: 'MATERIAL_CONSUMPTION',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Record material consumption',
    subtitle: 'Choose the project; the issues still to be accounted for can then be picked.',
    fields: [
      f.look('projectId', 'Project', 'project', true, 'full'),
      f.radio('consumedBy', 'Who used it?', [['SUBCONTRACTOR', 'A subcontractor'], ['OWN_LABOUR', 'Our own labour']]),
      { ...f.look('subcontractorId', 'Subcontractor', 'subcontractor', true, 'full'),
        visibleWhen: { field: 'consumedBy', equals: ['SUBCONTRACTOR'] } },
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('projectId', 'Project', 'project', true),
        f.date('documentDate', 'Recorded on', true),
        f.date('periodFrom', 'Period from'),
        f.date('periodTo', 'Period to'),
        f.choice('consumedBy', 'Who used it?', [['SUBCONTRACTOR', 'A subcontractor'], ['OWN_LABOUR', 'Our own labour']], false),
        { ...f.look('subcontractorId', 'Subcontractor', 'subcontractor'),
          visibleWhen: { field: 'consumedBy', equals: ['SUBCONTRACTOR'] } },
        { ...f.look('workProgressId', 'Against measured work (JMR)', 'work_progress'),
          hint: 'The work progress this material went into. What it measured is what should have been used.' },
        { ...f.look('returnWarehouseId', 'Return the balance to', 'warehouse'),
          hint: 'Left empty, what comes back stays in the site store for the next issue.' },
        f.look('recordedById', 'Recorded by', 'employee'),
        jobType(),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'What was issued, and what became of it', n: '02',
    columns: [
      col.seq(), col.item('Material', false), col.desc(),
      { key: 'sourceQty', label: 'Issued', type: 'qty', width: 100, align: 'right' },
      { key: 'openQty', label: 'To account', type: 'qty', width: 110, align: 'right' },
      { key: 'usedQty', label: 'Used', type: 'qty', width: 100, align: 'right', editable: true,
        hint: 'What went into the work.' },
      { key: 'wastageQty', label: 'Wastage', type: 'qty', width: 100, align: 'right', editable: true },
      { key: 'wastageReason', label: 'Why wasted', type: 'text', width: 200, editable: true,
        options: WASTAGE_REASONS.map(([value, label]) => ({ value, label })) },
      { key: 'returnQty', label: 'Returned', type: 'qty', width: 104, align: 'right', editable: true,
        hint: 'What comes back to the store, to be issued again.' },
      { key: 'quantity', label: 'Accounted', type: 'qty', width: 110, align: 'right',
        formula: { sum: ['usedQty', 'wastageQty', 'returnQty'] } },
      { key: 'balanceQty', label: 'Still at site', type: 'qty', width: 110, align: 'right' },
      { key: 'theoreticalQty', label: 'As per JMR', type: 'qty', width: 110, align: 'right' },
      { key: 'wastagePct', label: 'Wastage %', type: 'text', width: 100, align: 'right' },
      col.unit(false), col.rate('Rate', false), col.amount('Value'),
    ],
    sources: [{ key: 'issue', label: 'Pick from what was issued', source: 'issue' }],
  },
  columns: baseColumns(null, 'Value'),
  filters: [
    ...baseFilters(null),
    { key: 'subcontractorId', label: 'Subcontractor', control: 'lookup', lookup: { entity: 'subcontractor' } },
  ],
  actions: BASIC_ACTIONS,
  rules: ['consumption_exceeds_issue', 'wastage_over_allowance'],
  requiresSetting: 'inventory_enabled',
  createsNext: [{ docType: 'material_transfer', label: 'Material transfer', whenStatus: 'APPROVED' }],
} as const)

/** docs/08 §3 — stock moving between two warehouses. */
export const warehouseTransfer = defineDocument({
  key: 'warehouse_transfer',
  group: 'Inventory',
  blurb: 'Transfer materials from one warehouse to another.',
  route: 'warehouse_trfs',
  label: 'Warehouse transfer',
  labelPlural: 'Warehouse transfers',
  numbering: 'WAREHOUSE_TRANSFER',
  numberPrefix: 'WT-',
  workflow: 'WAREHOUSE_TRANSFER',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Warehouse transfer',
    fields: [
      f.look('warehouseId', 'From warehouse', 'warehouse', true, 'full'),
      f.look('toWarehouseId', 'To warehouse', 'warehouse', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('warehouseId', 'From warehouse', 'warehouse', true),
        f.look('toWarehouseId', 'To warehouse', 'warehouse', true),
        f.date('documentDate', 'Transfer date', true),
        f.text('vehicleNo', 'Vehicle no'),
        // A transfer moves goods on a public road exactly as a challan does,
        // and the setting has always said the number is shown on both. Only
        // the challan had the fields.
        { ...f.text('ewayBillNo', 'e-Way bill no'), requiresSetting: 'ewaybill_enabled' },
        { ...f.choice('ewayBillStatus', 'e-Way bill status', [
          ['', 'Not raised'], ['GENERATED', 'Generated'], ['CANCELLED', 'Cancelled'],
          ['EXPIRED', 'Expired'],
        ], false), requiresSetting: 'ewaybill_enabled' },
        f.text('deliveryCoordinator', 'Delivery coordinator'),
        taxUnit(),
        interstate(),
        f.notes(),
      ] },
    ],
  },
  lines: quantityLines('Materials transferred', '02', 'Transferred'),
  columns: baseColumns(null, 'Value'),
  filters: [
    ...baseFilters(null),
    { key: 'warehouseId', label: 'From warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
  ],
  actions: BASIC_ACTIONS,
  rules: ['insufficient_stock'],
  requiresSetting: 'inventory_enabled',
} as const)

/** docs/08 §4 — the store asking the purchase team to buy stock. */
export const stockPurchaseRequest = defineDocument({
  key: 'stock_purchase_request',
  group: 'Inventory',
  blurb: 'Raise a requisition to the purchase team for stock purchases.',
  route: 'stock_pur_reqs',
  label: 'Stock purchase request',
  labelPlural: 'Stock purchase requests',
  numbering: 'STOCK_PURCHASE_REQUEST',
  numberPrefix: 'SPR-',
  workflow: 'STOCK_PURCHASE_REQUEST',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Stock purchase request',
    fields: [
      f.look('warehouseId', 'Warehouse', 'warehouse', true, 'full'),
      { ...taxUnit(true), width: 'full' as const },
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.look('warehouseId', 'Warehouse', 'warehouse', true),
        f.date('documentDate', 'Request date', true),
        f.date('requiredDate', 'Required by'),
        taxUnit(),
        f.look('requestedById', 'Requested by', 'employee'),
        f.notes(),
      ] },
    ],
  },
  lines: quantityLines('Materials wanted', '02', 'Quantity'),
  columns: [
    ...baseColumns(null, 'Estimated value'),
    { key: 'fulfilmentStatus', label: 'Purchase status', type: 'text', width: 190 },
  ],
  filters: [
    ...baseFilters(null),
    { key: 'warehouseId', label: 'Warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
  ],
  actions: [...BASIC_ACTIONS, 'preClose', 'undoPreClose'],
  rules: [],
  requiresSetting: 'inventory_enabled',
  createsNext: [{ docType: 'purchase_order', label: 'Purchase order', whenStatus: 'APPROVED' }],
} as const)

/** Why a count differs from the books (docs/24 §3.10). */
export const VARIANCE_REASONS = [
  ['COUNT_ERROR', 'Miscounted earlier'],
  ['ISSUE_NOT_RECORDED', 'Issued without a record'],
  ['RECEIPT_NOT_RECORDED', 'Received without a record'],
  ['DAMAGE', 'Damaged in store'],
  ['THEFT', 'Theft'],
  ['UNIT_MISMATCH', 'Counted in another unit'],
  ['OTHER', 'Other'],
] as const satisfies ReadonlyArray<[string, string]>

/**
 * docs/24 §3.10 — counting what is actually on the shelf.
 *
 * "Monthly one time or weekly one time should take stock audit." The book says
 * one thing and the rack another, and until somebody counts, the book is
 * believed. A count is taken for one store — a warehouse, or a project's site
 * store — either in full or as a cycle count of one ABC class (A weekly, B
 * monthly, C quarterly, by the company's settings). Each line starts from the
 * book quantity, bin by bin; the counter enters what is there; the variance
 * and its value follow. An approved count then drives the stock adjustment
 * that makes the book agree with the shelf.
 */
export const stockAudit = defineDocument({
  key: 'stock_audit',
  group: 'Inventory',
  blurb: 'Count what is on the shelf, bin by bin, and see where the books are wrong.',
  route: 'stock_audits',
  label: 'Stock audit',
  labelPlural: 'Stock audits',
  numbering: 'STOCK_AUDIT',
  numberPrefix: 'SAU-',
  workflow: 'STOCK_AUDIT',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Count stock',
    subtitle: 'Which store, and all of it or one class of material?',
    fields: [
      f.radio('auditAt', 'Where?', [['WAREHOUSE', 'A warehouse'], ['SITE', 'A project site store']]),
      { ...f.look('warehouseId', 'Warehouse', 'warehouse', true, 'full'),
        visibleWhen: { field: 'auditAt', equals: ['WAREHOUSE'] } },
      { ...f.look('projectId', 'Project', 'project', true, 'full'),
        visibleWhen: { field: 'auditAt', equals: ['SITE'] } },
      f.radio('auditType', 'What is being counted?', [
        ['FULL', 'Everything in the store'],
        ['CYCLE', 'One class — a cycle count'],
      ]),
      { ...f.choice('abcClass', 'Class', [['A', 'A'], ['B', 'B'], ['C', 'C'], ['S', 'S']]),
        visibleWhen: { field: 'auditType', equals: ['CYCLE'] } },
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        { ...f.choice('auditAt', 'Where?', [['WAREHOUSE', 'A warehouse'], ['SITE', 'A project site store']], false), lockAfterDraft: true },
        { ...f.look('warehouseId', 'Warehouse', 'warehouse'), lockAfterDraft: true,
          visibleWhen: { field: 'auditAt', equals: ['WAREHOUSE'] } },
        { ...f.look('projectId', 'Project', 'project'), lockAfterDraft: true,
          visibleWhen: { field: 'auditAt', equals: ['SITE'] } },
        f.date('documentDate', 'Counted on', true),
        f.choice('auditType', 'Count', [['FULL', 'Full count'], ['CYCLE', 'Cycle count of one class']], false),
        { ...f.choice('abcClass', 'Class', [['A', 'A'], ['B', 'B'], ['C', 'C'], ['S', 'S']], false),
          visibleWhen: { field: 'auditType', equals: ['CYCLE'] } },
        f.look('countedById', 'Counted by', 'employee', true),
        f.look('witnessedById', 'Witnessed by', 'employee'),
        { key: 'accuracyPct', label: 'Lines that agreed', control: 'text', width: 'half',
          hint: 'Worked out on save: the share of lines where the count matched the books.' },
        { key: 'varianceValue', label: 'Net variance value', control: 'text', width: 'half',
          hint: 'Worked out on save: gains less losses, at the book rate.' },
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Counted', n: '02',
    columns: [
      col.seq(), col.item('Material', false), col.desc(),
      { key: 'binCode', label: 'Bin', type: 'text', width: 110 },
      { key: 'bookQty', label: 'Book', type: 'qty', width: 104, align: 'right' },
      { ...col.qty('Counted'), hint: 'What is physically there. Fill all open quantities starts every line at the book figure.' },
      { key: 'varianceQty', label: 'Variance', type: 'qty', width: 104, align: 'right',
        formula: { subtract: ['quantity', 'bookQty'] } },
      col.unit(false),
      col.rate('Book rate', false),
      { key: 'varianceValue', label: 'Variance value', type: 'money', width: 140, align: 'right' },
      { key: 'varianceReason', label: 'Why it differs', type: 'text', width: 200, editable: true,
        options: VARIANCE_REASONS.map(([value, label]) => ({ value, label })) },
    ],
    sources: [{ key: 'book', label: 'Load the book stock', source: 'book' }],
  },
  columns: [
    ...baseColumns(null, 'Counted value'),
    { key: 'auditType', label: 'Count', type: 'text', width: 110 },
    { key: 'accuracyPct', label: 'Agreed', type: 'text', width: 100, align: 'right' },
    { key: 'varianceValue', label: 'Variance', type: 'text', width: 130, align: 'right' },
  ],
  filters: [
    ...baseFilters(null),
    { key: 'warehouseId', label: 'Warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
  ],
  actions: BASIC_ACTIONS,
  rules: ['variance_without_reason'],
  requiresSetting: 'inventory_enabled',
  createsNext: [{ docType: 'stock_adjustment', label: 'Stock adjustment', whenStatus: 'APPROVED' }],
} as const)

/** docs/08 §5 — correcting what the system thinks is on the shelf. */
export const stockAdjustment = defineDocument({
  key: 'stock_adjustment',
  group: 'Inventory',
  blurb: 'Use a stock adjustment to set up all warehouses with the correct opening inventory.',
  route: 'stock_adjs',
  label: 'Stock adjustment',
  labelPlural: 'Stock adjustments',
  numbering: 'STOCK_ADJUSTMENT',
  numberPrefix: 'SA-',
  numberWidth: 3,
  workflow: 'STOCK_ADJUSTMENT',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Stock adjustment',
    subtitle: 'A warehouse — or, for a count at a site, the project whose site store it is.',
    fields: [
      f.radio('adjustmentType', 'Which way?', [['INCREASE', 'Increase'], ['DECREASE', 'Decrease']]),
      f.look('warehouseId', 'Warehouse', 'warehouse', false, 'full'),
      f.look('projectId', 'Site store of project', 'project', false, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        { ...f.choice('adjustmentType', 'Which way?', [['INCREASE', 'Increase'], ['DECREASE', 'Decrease']]),
          lockAfterDraft: true,
          hint: 'An increase brings stock on; a decrease takes it off, and must be picked from what is on hand.' },
        { ...f.look('warehouseId', 'Warehouse', 'warehouse'),
          hint: 'Leave it empty to adjust a project’s site store, and name the project.' },
        { ...f.look('projectId', 'Site store of project', 'project'),
          hint: 'For a count taken at a project site.' },
        // docs/24 §3.10 — the count this adjustment makes the books agree with.
        { ...f.look('stockAuditId', 'From stock audit', 'stock_audit'),
          hint: 'Name the approved count, then load its variances.' },
        f.date('documentDate', 'Adjustment date', true),
        f.text('reason', 'Reason', 'full', true),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Materials adjusted', n: '02',
    columns: [col.seq(), col.item(), col.desc(), col.brand(), col.qty('Adjust by'), col.unit(), col.rate('Value rate'), col.bin(), col.amount()],
    sources: [
      { key: 'stock', label: 'Pick from stock', source: 'stock' },
      { key: 'audit', label: 'Load the audit’s variances', source: 'audit' },
      { key: 'excel', label: 'Import a template', source: 'excel' },
    ],
  },
  columns: [
    ...baseColumns(null, 'Value'),
    { key: 'adjustmentType', label: 'Direction', type: 'text', width: 130 },
  ],
  filters: [
    ...baseFilters(null),
    { key: 'warehouseId', label: 'Warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
  ],
  actions: BASIC_ACTIONS,
  rules: ['insufficient_stock', 'stock_location_missing', 'adjustment_exceeds_count'],
  requiresSetting: 'inventory_enabled',
} as const)
